import React, { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import {
  formatCountryOptions,
  formatMobileCodeOptions,
  formatCurrencyOptions,
} from "../../lib/CountryDataFromatter";
import { fetchCategories } from "../../api/categoryApi";
import config from "../../config";

const getFlagUrl = (countryCode) =>
  `https://flagcdn.com/16x12/${countryCode.toLowerCase()}.png`;
// Base selector component
const BaseSelector = ({
  options,
  value,
  onChange,
  onBlur,
  name,
  renderOption,
  placeholder,
  error,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredOptions = useMemo(() => {
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()) || option.value.includes(searchTerm.toLowerCase()) || option.flag.includes(searchTerm.toLowerCase()) || option.dialCode.includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  const selectedOption = options.find((opt) => String(opt.value) === String(value));

  return (
    <div className="relative mb-3">
      <div
        className={`max-w-[200px] border rounded-full ${
          error ? "border-red-500" : "border-gray-300"
        } 
          ${disabled ? "bg-gray-100" : "bg-white"}`}
      >
        <div
          className="flex items-center justify-between p-2 cursor-pointer"
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <div className="flex items-center gap-2 flex-1">
            {value && selectedOption ? (
              renderOption(selectedOption)
            ) : (
              <span className="text-gray-400">{placeholder}</span>
            )}
          </div>
          <div className="text-gray-400">
            <svg
              className={`w-4 h-4 transition-transform ${
                isOpen ? "transform rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M19 9l-7 7-7-7"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
              />
            </svg>
          </div>
        </div>

        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
            <div className="p-2 border-b">
              <div className="flex items-center gap-2 px-2 py-1 bg-gray-50 rounded">
                <Search className="w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  className="w-full bg-transparent border-none focus:outline-none text-sm"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="max-h-60 overflow-auto">
              {filteredOptions.map((option) => (
                <div
                  key={option.value+option.flag}
                  className="p-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                    setSearchTerm("");
                  }}
                >
                  {renderOption(option)}
                </div>
              ))}
              {filteredOptions.length === 0 && (
                <div className="p-2 text-gray-400 text-center">
                  No results found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
};
export const Categoryelector = ({
  value,
  onChange,
  error,
  disabled,
  name,
  onBlur,
}) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const loadCategories = async () => {
      const fetchedCategories = await fetchCategories({});
      const formattedCategories = fetchedCategories.map((category) => ({
        value: category.category_id,
        label: category.en_name,
        photo: category.photo,
      }));
      setCategories(formattedCategories);
      setLoading(false);
    };
    loadCategories();
  }, []);
  const categoryOptions = useMemo(() => categories, [categories]);
  return (
    <BaseSelector
      options={categoryOptions}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      name={name}
      error={error}
      disabled={disabled}
      placeholder="Select Category"
      renderOption={(option) => (
        <div className="flex items-center gap-2">
          <img
            src={`${config.apiUrl}/assets/uploads/${option.photo}`}
            alt={`flag of ${option.value}`}
            style={{ width: "32px", height:"32px", maxWidth:"50px;" }}
          />
          <span className="text-black">{option.label}</span>
        </div>
      )}
    />
  );
};
// Country Selector Component
export const CountrySelector = ({
  value,
  onChange,
  error,
  disabled,
  name,
  onBlur,
}) => {
  const countryOptions = useMemo(() => formatCountryOptions(), []);
  return (
    <BaseSelector
      options={countryOptions}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      name={name}
      error={error}
      disabled={disabled}
      placeholder="Select country"
      renderOption={(option) => (
        <div className="flex items-center gap-2">
          <img
            src={getFlagUrl(option.flag)}
            alt={`flag of ${option.flag}`}
            style={{ width: "16px", height: "auto" }}
          />
          <span>{option.label}</span>
        </div>
      )}
    />
  );
};

// Mobile Code Selector Component
export const MobileCodeSelector = ({
  value,
  onChange,
  error,
  disabled,
  name,
  onBlur,
}) => {
  const mobileCodeOptions = useMemo(() => {
  const options = formatMobileCodeOptions();
  console.log("Mobile Code Options:", options);
  return options;
}, []);

  return (
    <BaseSelector
      options={mobileCodeOptions}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      name={name}
      error={error}
      disabled={disabled}
      renderOption={(option) => (
        <div className="flex items-center gap-2">
          <img
            src={getFlagUrl(option.flag)}
            alt={`flag of ${option.flag}`}
            style={{ width: "16px", height: "auto" }}
          />
          <span>{option.dialCode}</span>
        </div>
      )}
    />
  );
};

// Currency Selector Component
export const CurrencySelector = ({
  value,
  onChange,
  error,
  disabled,
  name,
  onBlur,
}) => {
  const currencyOptions = useMemo(() => formatCurrencyOptions(), []);

  return (
    <BaseSelector
      options={currencyOptions}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      name={name}
      error={error}
      disabled={disabled}
      placeholder="Select currency"
      renderOption={(option) => (
        <div className="flex items-center gap-2 z-50">
          <img
            src={getFlagUrl(option.flag)}
            alt={`flag of ${option.flag}`}
            style={{ width: "16px", height: "auto" }}
          />
          <span>{option.value}</span>
        </div>
      )}
    />
  );
};

// Example usage with react-hook-form
export default function Example() {
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedMobileCode, setSelectedMobileCode] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("");

  return (
    <div className="space-y-4 p-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Country
        </label>
        <CountrySelector
          value={selectedCountry}
          onChange={setSelectedCountry}
          error={null}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Mobile Code
        </label>
        <MobileCodeSelector
          value={selectedMobileCode}
          onChange={setSelectedMobileCode}
          error={null}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Currency
        </label>
        <CurrencySelector
          value={selectedCurrency}
          onChange={setSelectedCurrency}
          error={null}
        />
      </div>
    </div>
  );
}
